export function isDirectErrorPage(pathname, locales = []) {
  const segments = pathname.split("/").filter(Boolean);
  if (locales.includes(segments[0])) segments.shift();
  return segments.length === 1 && ["500", "_error"].includes(segments[0]);
}

// Probe the document URL, not /500 or a health endpoint: the page's own data
// dependencies must recover before we replace the error screen.
export function createPageRecovery(
  browser,
  document,
  locales = [],
  autoRetry = true,
) {
  const target = browser.location.pathname + browser.location.search;
  const canRetry = !isDirectErrorPage(browser.location.pathname, locales);
  let stopped = false;
  let attempt = 0;
  let retryTimer;
  let requestTimer;
  let request;

  const isAvailable = () =>
    document.visibilityState === "visible" &&
    browser.navigator.onLine !== false;
  const isCurrentPage = () =>
    target === browser.location.pathname + browser.location.search;

  function cancelPending() {
    browser.clearTimeout(retryTimer);
    browser.clearTimeout(requestTimer);
    request?.abort();
    request = null;
  }

  function schedule(
    delay = Math.min(15000 * 2 ** attempt, 60000) * (0.9 + Math.random() * 0.1),
  ) {
    if (!stopped && autoRetry && canRetry && isAvailable() && isCurrentPage()) {
      retryTimer = browser.setTimeout(check, delay);
    }
  }

  async function check() {
    if (stopped || request || !isAvailable() || !isCurrentPage()) return;
    const controller = new AbortController();
    request = controller;
    requestTimer = browser.setTimeout(() => controller.abort(), 35000);

    try {
      const response = await browser.fetch(target, {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        redirect: "manual",
        headers: { Accept: "text/html" },
        signal: controller.signal,
      });
      // Headers suffice; don't download the full page twice on recovery.
      response.body?.cancel().catch(() => {});
      if (
        !stopped &&
        !controller.signal.aborted &&
        isAvailable() &&
        isCurrentPage() &&
        response.status === 200 &&
        !response.redirected &&
        response.headers.get("content-type")?.includes("text/html")
      ) {
        retry();
      }
    } catch {
      // An unavailable network or another server error leaves this view in place.
    } finally {
      if (request === controller) {
        browser.clearTimeout(requestTimer);
        request = null;
        attempt = Math.min(attempt + 1, 2);
        schedule();
      }
    }
  }

  function resume() {
    cancelPending();
    attempt = 0;
    schedule(0);
  }

  function stop() {
    stopped = true;
    cancelPending();
    document.removeEventListener("visibilitychange", resume);
    browser.removeEventListener("online", resume);
    browser.removeEventListener("offline", resume);
  }

  function retry() {
    stop();
    if (canRetry && isCurrentPage()) browser.location.reload();
  }

  if (autoRetry && canRetry) {
    document.addEventListener("visibilitychange", resume);
    browser.addEventListener("online", resume);
    browser.addEventListener("offline", resume);
    schedule();
  }

  return { retry, stop, canRetry };
}
